import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { OAuthService } from 'src/app/shared/services/oauth.service';
import { environment } from 'src/environments/environment';
import { BillingSummary, ProjectSummary, SummaryRow, SummaryGroupingOption, GroupingOptionInfo, OrphanRepairResult } from './models/billing-models';
import { FileMetadata } from '../Projects/Arrivals/arrivalNotice.model';
import { NetvisorClientInvoice } from './models/netvisor-models';
import { WorkTypeClass } from 'src/app/shared/Models/worktype-model';
import { BillingSummaryHistoryResponse } from './models/summary-history.model';
/** A file the billing upload refuses before sending (type or 5 MB Netvisor limit). */
export class AttachmentValidationError extends Error {
  constructor(public readonly fileName: string) {
    super(`Attachment not allowed: ${fileName}`);
  }
}

@Injectable({
  providedIn: 'root'
})
export class BillingService {



  constructor(
    private http:HttpClient) { }


    getBillngSummaryTEST(
      projectId:number,
      startDate: Date,
      endDate: Date,
      separateEquipment:boolean=false,
      saveSummary:boolean=false,
      groupBy: SummaryGroupingOption = SummaryGroupingOption.None){

        let params = new HttpParams()
          .set('projectId', projectId.toString())
          .set('startDate', startDate.toISOString())
          .set('endDate', endDate.toISOString())
          .set('separateEquipment', separateEquipment.toString())
          .set('saveSummary', saveSummary.toString());

        if (groupBy !== SummaryGroupingOption.None) {
          params = params.set('groupBy', groupBy.toString());
        }

        // Saving creates summaries, so it is a POST (POST api/billing); a preview stays a read (GET, saveSummary=false).
        const request = saveSummary
          ? this.http.post<ProjectSummary>(`${environment.apiRoot}billing`, null, { params })
          : this.http.get<ProjectSummary>(`${environment.apiRoot}billing`, { params });
        return request.pipe(map(data => new ProjectSummary(data)));
      }

  getGroupingOptions(): Observable<GroupingOptionInfo[]> {
    return this.http.get<GroupingOptionInfo[]>(`${environment.apiRoot}billing/GetGroupingOptions`);
  }

  /**
   * Whether a project summary exists for the week. With startDate/endDate the backend checks overlap of that
   * exact range (same predicate as summary creation); without them the old week-number check applies.
   */
  summaryExists(projectId: number, weekNumber: number, startDate?: Date, endDate?: Date) {
    let params = new HttpParams();
    if (startDate && endDate) {
      params = params.set('startDate', startDate.toISOString()).set('endDate', endDate.toISOString());
    }
    return this.http.get<boolean>(`${environment.apiRoot}billing/summaryexists/${projectId}/${weekNumber}`, { params });
  }
  // testSummaryMessage(summaryId:string){
  //   return this.http.get<string>(`${environment.apiRoot}billing/testsummarymessage/${summaryId}`);
  // }
  getProjectSummaries(selectedProject: number): Observable<ProjectSummary[]> {

        return this.http.get<ProjectSummary[]>(`${environment.apiRoot}billing/getprojectsummaries/${selectedProject}`).pipe(map(data => data.map(item => new ProjectSummary(item))));
  }

  getSummary(summaryId: number): Observable<ProjectSummary> {

    return this.http.get<ProjectSummary>(`${environment.apiRoot}billing/${summaryId}`).pipe(map(data => new ProjectSummary(data)));
  }
  getClientSummary(summaryId: string): Observable<BillingSummary> {

    return this.http.get<BillingSummary>(`${environment.apiRoot}billing/getClientSummary/${summaryId}`);
  }
  updateBillingSummary(summary: BillingSummary) {
    return this.http.put<BillingSummary>(`${environment.apiRoot}billing/updatesummary/${summary.id}`, summary)
      .pipe(map(data => new BillingSummary(data)));
  }
  updateSummaryRow(summaryRow: SummaryRow) {
    return this.http.put<SummaryRow>(`${environment.apiRoot}billing/updateSummaryRow/${summaryRow.id}`, summaryRow);
  }
  acceptSummary(summaryId: string) {
    return this.http.put<BillingSummary>(`${environment.apiRoot}billing/acceptsummary/${summaryId}`, null);
  }
  /** Marks a confirmed, unsent summary as billed manually (optional external reference). */
  markBilled(summaryId: string, netvisorId?: string | null): Observable<BillingSummary> {
    const body = netvisorId ? { netvisorId } : {};
    return this.http.put<BillingSummary>(`${environment.apiRoot}billing/markBilled/${summaryId}`, body)
      .pipe(map(data => new BillingSummary(data)));
  }
  clearConfirmation(summaryId: string) {
    return this.http.put<BillingSummary>(`${environment.apiRoot}billing/clearconfirmation/${summaryId}`, null);
  }
  regenerateCleaningPdf(summaryId: string): Observable<BillingSummary> {
    return this.http.post<BillingSummary>(`${environment.apiRoot}billing/regenerateCleaningPdf/${summaryId}`, null)
      .pipe(map(data => new BillingSummary(data)));
  }
  previewCleaningPdf(summaryId: string): Observable<Blob> {
    return this.http.get(`${environment.apiRoot}billing/previewCleaningPdf/${summaryId}`, { responseType: 'blob' });
  }
  regenerateSummaryPdf(summaryId: string): Observable<BillingSummary> {
    return this.http.post<BillingSummary>(`${environment.apiRoot}billing/regenerateSummaryPdf/${summaryId}`, null)
      .pipe(map(data => new BillingSummary(data)));
  }
  previewSummaryPdf(summaryId: string): Observable<Blob> {
    return this.http.get(`${environment.apiRoot}billing/previewSummaryPdf/${summaryId}`, { responseType: 'blob' });
  }
sendInvoice(summaryId: string) {
  return this.http.post<NetvisorClientInvoice>(`${environment.apiRoot}billing/invoice/clientsummary/${summaryId}`, null);
}
  deleteProjectSummary(summaryId: number): Observable<any> {
    return this.http.delete(`${environment.apiRoot}billing/deleteProjectSummary/${summaryId}`);
  }
  deleteClientSummary(summaryId: string): Observable<any> {
    return this.http.delete(`${environment.apiRoot}billing/deleteClientSummary/${summaryId}`);
  }
  getProjectSummariesForDateRange(startDate: Date, endDate: Date): Observable<ProjectSummary[]> {
    return this.http.get<ProjectSummary[]>(`${environment.apiRoot}billing/getprojectsummaries/${startDate.toISOString()}/${endDate.toISOString()}`).pipe(map(data => data.map(item => new ProjectSummary(item))));
  }

  getEmptySummary(projectSummaryId: number,projectId:number, clientId:string,summaryTitle:string, startDate:Date, endDate:Date): Observable<BillingSummary> {
    return this.http.post<BillingSummary>(`${environment.apiRoot}billing/getemptysummary/${projectSummaryId}/${projectId}/${clientId}/${encodeURIComponent(summaryTitle)}/${startDate.toISOString()}/${endDate.toISOString()}`, null);
  }

  private readonly ALLOWED_UPLOAD_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
  ];
  private readonly MAX_UPLOAD_BYTES = 5 * 1024 * 1024; // 5 MB (Netvisor limit)

  private validateUploadFile(file: File): string | null {
    if (!this.ALLOWED_UPLOAD_TYPES.includes(file.type)) {
      return `File type '${file.type}' is not allowed. Allowed types: PDF, images, Word, Excel.`;
    }
    if (file.size > this.MAX_UPLOAD_BYTES) {
      return `File exceeds the maximum allowed size of 5 MB.`;
    }
    return null;
  }

  uploadSummaryAttachment(summaryId: string, file: File): Observable<FileMetadata> {
    const validationError = this.validateUploadFile(file);
    if (validationError) {
      return throwError(() => new Error(validationError));
    }
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<FileMetadata>(`${environment.apiRoot}billing/uploadAttachment/${summaryId}`, formData);
  }
  /**
   * Uploads all files in one request. The endpoint holds the summary's send lock and refuses a parallel request
   * (409 SUMMARY_SEND_IN_PROGRESS), so one request per file loses every file but the first.
   */
  uploadExtraAttachments(summaryId: string, files: File[]): Observable<FileMetadata[]> {
    const invalid = files.find((file) => this.validateUploadFile(file));
    if (invalid) {
      return throwError(() => new AttachmentValidationError(invalid.name));
    }
    const formData = new FormData();
    files.forEach((file) => formData.append('file', file, file.name));
    return this.http.post<FileMetadata[]>(`${environment.apiRoot}billing/uploadExtraAttachments/${summaryId}`, formData);
  }
  deleteAttachment(summaryId:string, file:FileMetadata){
    return this.http.delete(`${environment.apiRoot}billing/deleteAttachment/${summaryId}/${file.fileID}`);
  }
  getNetvisorProducts(): Observable<WorkTypeClass[]> {
    return this.http.get<WorkTypeClass[]>(`${environment.apiRoot}WorkTypeClass`);
  }
  getManagerSummaries(page: number, pageSize: number): Observable<{ items: BillingSummary[], totalCount: number }> {
    return this.http.get<{ items: BillingSummary[], totalCount: number }>(
      `${environment.apiRoot}billing/ManagerSummaries/${page}/${pageSize}`
    ).pipe(
      map(response => ({
        items: response.items.map(item => new BillingSummary(item)).sort((a, b) => new Date(b.createdDate!).getTime() - new Date(a.createdDate!).getTime()),
        totalCount: response.totalCount
      }))
    );
  }
  getUnconfirmedSummaries() {
    return this.http.get<BillingSummary[]>(`${environment.apiRoot}billing/ManagerSummaries/unconfirmed`)
      .pipe(
        map(data =>
          data
            .map(item => new BillingSummary(item))
            .sort((a, b) => new Date(b.createdDate!).getTime() - new Date(a.createdDate!).getTime())
        )
      );
  }
  getConfirmedSummaries() {
    return this.http.get<BillingSummary[]>(`${environment.apiRoot}billing/ManagerSummaries/unconfirmed?confirmed=${true}`)
      .pipe(
        map(data =>
          data
            .map(item => new BillingSummary(item))
            .sort((a, b) => new Date(b.createdDate!).getTime() - new Date(a.createdDate!).getTime())
        )
      );
  }

  getManagerUnconfirmedCount(confirmed:boolean, billed:boolean): Observable<number> {
    return this.http.get<number>(`${environment.apiRoot}billing/ManagerSummaryCount/${confirmed}/${billed}`);
  }
  getInvoiceData(summaryId: string): Observable<NetvisorClientInvoice> {
    return this.http.get<NetvisorClientInvoice>(`${environment.apiRoot}billing/invoiceData/${summaryId}`);
  }
  getNetvisorClientInvoices(summaryIds: string[]): Observable<NetvisorClientInvoice[]> {
    return this.http.post<NetvisorClientInvoice[]>(`${environment.apiRoot}billing/invoiceDataFor`, summaryIds );
  }

  /**
   * Creates a credit summary (hyvityslasku) for an existing billed summary.
   * Used for refunds and re-billing scenarios.
   * @param originalSummaryId The ID of the original billed summary to credit
   * @returns The newly created credit summary
   */
  createCreditSummary(originalSummaryId: string): Observable<BillingSummary> {
    return this.http.post<BillingSummary>(
      `${environment.apiRoot}billing/createCreditSummary/${originalSummaryId}`,
      null
    ).pipe(map(data => new BillingSummary(data)));
  }

  /**
   * Recalculates a billing summary from its source hour entries.
   * This replaces all existing rows with freshly calculated ones from the entries.
   * @param summaryId The ID of the summary to recalculate
   * @returns The updated summary with recalculated rows
   */
  /** Recalculates a saved week with the project's settings; confirmed and sent summaries, credit notes and hand-added summaries stay. */
  recalculateWeek(projectSummaryId: number): Observable<ProjectSummary> {
    return this.http.post<ProjectSummary>(
      `${environment.apiRoot}billing/recalculateWeek/${projectSummaryId}`,
      null
    ).pipe(map(data => new ProjectSummary(data)));
  }

  recalculateSummary(summaryId: string): Observable<BillingSummary> {
    return this.http.post<BillingSummary>(
      `${environment.apiRoot}billing/recalculateSummary/${summaryId}`,
      null
    ).pipe(map(data => new BillingSummary(data)));
  }

  previewOrphanRepair(): Observable<OrphanRepairResult> {
    return this.http.post<OrphanRepairResult>(
      `${environment.apiRoot}billing/repairOrphanedSummaries`,
      null,
    );
  }

  executeOrphanRepair(): Observable<OrphanRepairResult> {
    return this.http.post<OrphanRepairResult>(
      `${environment.apiRoot}billing/repairOrphanedSummaries`,
      null,
      { params: new HttpParams().set('execute', true) },
    );
  }

  createSummaryContainerForOrphan(clientSummaryId: string): Observable<ProjectSummary> {
    return this.http.post<ProjectSummary>(
      `${environment.apiRoot}billing/createSummaryContainerForOrphan/${clientSummaryId}`,
      null,
    ).pipe(map(data => new ProjectSummary(data)));
  }

  /** Change history of one client summary, newest first, paged by skip/take (server clamps take to 50). */
  getClientSummaryHistory(summaryId: string, skip = 0, take = 20): Observable<BillingSummaryHistoryResponse> {
    const params = new HttpParams().set('skip', skip).set('take', take);
    return this.http.get<BillingSummaryHistoryResponse>(`${environment.apiRoot}billing/clientSummary/${summaryId}/history`, { params });
  }
}
